import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Button,
	Center,
	Container,
	Heading,
	Input,
	Spinner,
	Stack,
	Text,
	Textarea,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	IconButton,
	useToast,
	Tabs,
	TabList,
	TabPanels,
	Tab,
	TabPanel,
	Collapse,
	Checkbox,
	Select,
	Image,
	Avatar,
	Badge,
} from "@chakra-ui/react";
import { MdDelete, MdEdit, MdAdd, MdLogout, MdDragHandle, MdUpload } from "react-icons/md";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useCmsApi } from "../../services/useCmsApi";
import { useCmsAdminApi, ICmsUser } from "../../services/useCmsAdminApi";
import { IArticle, IArticleLink } from "../../models/IArticle";
import { ITestimonial, IPricingTier, IPartner, ITeamMember, ICaseStudy, ICaseStudyImage, ISiteSetting } from "../../models/ICmsModels";


const getToken = (): string => sessionStorage.getItem("cmsToken") || "";

export const ALLOWED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp"]);
export const MAX_FILE_SIZE = 5 * 1024 * 1024;

export const isValidUrl = (url: string | undefined | null): boolean => {
	if (!url) return false;
	try { new URL(url); return true; } catch { return false; }
};

export const isHttpUrl = (url: string | undefined | null): boolean => {
	if (!url) return false;
	try { const u = new URL(url); return u.protocol === "https:" || u.protocol === "http:"; } catch { return false; }
};

export const validateImageFile = (file: File, toastFn: ReturnType<typeof useToast>): boolean => {
	if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
		toastFn({ title: "Invalid file type", description: "Only JPG, PNG, GIF, and WebP images are allowed.", status: "error" });
		return false;
	}
	if (file.size > MAX_FILE_SIZE) {
		toastFn({ title: "File too large", description: "Maximum file size is 5MB.", status: "error" });
		return false;
	}
	return true;
};

// ---- Generic CRUD Table ----

interface CrudTableProps<T> {
	title: string;
	items: T[];
	columns: { key: keyof T; label: string }[];
	onDelete: (item: T) => void;
	onEdit?: (item: T) => void;
	onRefresh: () => void;
	idField: keyof T;
}

function CrudTable<T>({ title, items, columns, onDelete, onEdit, onRefresh, idField }: CrudTableProps<T>) {
	return (
		<Stack spacing="4">
			<Stack direction="row" justify="space-between" align="center">
				<Heading size="md">{title}</Heading>
				<Button size="sm" onClick={onRefresh}>Refresh</Button>
			</Stack>
			<Box overflowX="auto">
				<Table size="sm">
					<Thead>
						<Tr>
							{columns.map((col) => (
								<Th key={String(col.key)}>{col.label}</Th>
							))}
							<Th>Actions</Th>
						</Tr>
					</Thead>
					<Tbody>
						{items.map((item, i) => (
							<Tr key={String((item as any)[idField]) || i}>
								{columns.map((col) => (
									<Td key={String(col.key)} maxW="300px" isTruncated>
										{String((item as any)[col.key] ?? "")}
									</Td>
								))}
								<Td>
									{onEdit && (
										<IconButton
											aria-label="Edit"
											icon={<MdEdit />}
											size="sm"
											colorScheme="blue"
											variant="ghost"
											onClick={() => onEdit(item)}
											mr="1"
										/>
									)}
									<IconButton
										aria-label="Delete"
										icon={<MdDelete />}
										size="sm"
										colorScheme="red"
										variant="ghost"
										onClick={() => onDelete(item)}
									/>
								</Td>
							</Tr>
						))}
					</Tbody>
				</Table>
			</Box>
			{items.length === 0 && (
				<Text color="gray.500" textAlign="center" py="4">No items yet. Add data via the API or this panel.</Text>
			)}
		</Stack>
	);
}


function SortableRow<T extends { id: number }>({ item, columns, onEdit, onDelete }: { item: T; columns: { key: keyof T; maxW?: string }[]; onEdit?: (t: T) => void; onDelete: (t: T) => void }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
	const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
	return (
		<Tr ref={setNodeRef} style={style}>
			<Td width="40px" cursor="grab" {...attributes} {...listeners}><MdDragHandle /></Td>
			{columns.map((col) => (
				<Td key={String(col.key)} maxW={col.maxW || "200px"} isTruncated>{String((item as any)[col.key] ?? "")}</Td>
			))}
			<Td>
				{onEdit && <IconButton aria-label="Edit" icon={<MdEdit />} size="sm" colorScheme="blue" variant="ghost" onClick={() => onEdit(item)} mr="1" />}
				<IconButton aria-label="Delete" icon={<MdDelete />} size="sm" colorScheme="red" variant="ghost" onClick={() => onDelete(item)} />
			</Td>
		</Tr>
	);
}

export const CmsDashboardPage = (): JSX.Element => {
	const navigate = useNavigate();
	const toast = useToast();
	const cmsApi = useCmsApi();
	const adminApi = useCmsAdminApi();

	const [email, setEmail] = useState(sessionStorage.getItem("cmsEmail") || "");

	// Data state
	const [articles, setArticles] = useState<IArticle[]>([]);
	const [testimonials, setTestimonials] = useState<ITestimonial[]>([]);
	const [pricingTiers, setPricingTiers] = useState<IPricingTier[]>([]);
	const [partners, setPartners] = useState<IPartner[]>([]);
	const [teamMembers, setTeamMembers] = useState<ITeamMember[]>([]);
	const [caseStudies, setCaseStudies] = useState<ICaseStudy[]>([]);
	const [siteSettings, setSiteSettings] = useState<ISiteSetting[]>([]);
	const [cmsUsers, setCmsUsers] = useState<ICmsUser[]>([]);
	const [isLoadingData, setIsLoadingData] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);

	useEffect(() => {
		const token = getToken();
		if (!token) { navigate("/cms/login"); return; }
		try {
			const raw = token.split('.')[1];
			const b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
			const payload = JSON.parse(atob(b64));
			if (payload.exp && payload.exp * 1000 < Date.now()) {
				sessionStorage.removeItem("cmsToken");
				navigate("/cms/login");
				return;
			}
		} catch { sessionStorage.removeItem("cmsToken"); navigate("/cms/login"); return; }
		loadAll();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const loadAll = async () => {
		setIsLoadingData(true);
		setLoadError(null);
		const results = await Promise.allSettled([
			cmsApi.getArticles(100).then(setArticles),
			cmsApi.getTestimonials().then(setTestimonials),
			cmsApi.getPricingTiers().then(setPricingTiers),
			cmsApi.getPartners().then(setPartners),
			cmsApi.getTeamMembers().then(setTeamMembers),
			cmsApi.getCaseStudies().then(setCaseStudies),
			cmsApi.getSiteSettings().then(setSiteSettings),
			adminApi.getUsers(getToken()).then(setCmsUsers),
		]);
		const failures = results.filter((r) => r.status === "rejected");
		if (failures.length > 0) {
			const msg = `${failures.length} API call(s) failed to load.`;
			setLoadError(msg);
			toast({ title: "Load error", description: msg, status: "error", duration: 5000 });
		}
		setIsLoadingData(false);
	};

	const handleLogout = () => {
		sessionStorage.removeItem("cmsToken");
		sessionStorage.removeItem("cmsEmail");
		navigate("/cms/login");
	};

	const handleDeleteArticle = async (item: IArticle) => {
		// TODO: Replace window.confirm with Chakra AlertDialog for better UX
		if (!window.confirm(`Delete article "${item.title}"?`)) return;
		try {
			const ok = await adminApi.deleteArticle(getToken(), item.id);
			if (ok) { toast({ title: "Deleted", status: "success" }); cmsApi.getArticles(100).then(setArticles); }
			else { toast({ title: "Delete failed", status: "error" }); }
		} catch { toast({ title: "Delete failed", status: "error" }); }
	};

	const handleDeleteTestimonial = async (item: ITestimonial) => {
		// TODO: Replace window.confirm with Chakra AlertDialog for better UX
		if (!window.confirm(`Delete testimonial from "${item.name}"?`)) return;
		try {
			const ok = await adminApi.deleteTestimonial(getToken(), item.id);
			if (ok) { toast({ title: "Deleted", status: "success" }); cmsApi.getTestimonials().then(setTestimonials); }
			else { toast({ title: "Delete failed", status: "error" }); }
		} catch { toast({ title: "Delete failed", status: "error" }); }
	};

	const handleDeletePricingTier = async (item: IPricingTier) => {
		// TODO: Replace window.confirm with Chakra AlertDialog for better UX
		if (!window.confirm(`Delete pricing tier "${item.title}"?`)) return;
		try {
			const ok = await adminApi.deletePricingTier(getToken(), item.id);
			if (ok) { toast({ title: "Deleted", status: "success" }); cmsApi.getPricingTiers().then(setPricingTiers); }
			else { toast({ title: "Delete failed", status: "error" }); }
		} catch { toast({ title: "Delete failed", status: "error" }); }
	};

	const handleDeletePartner = async (item: IPartner) => {
		// TODO: Replace window.confirm with Chakra AlertDialog for better UX
		if (!window.confirm(`Delete partner "${item.name}"?`)) return;
		try {
			const ok = await adminApi.deletePartner(getToken(), item.id);
			if (ok) { toast({ title: "Deleted", status: "success" }); cmsApi.getPartners().then(setPartners); }
			else { toast({ title: "Delete failed", status: "error" }); }
		} catch { toast({ title: "Delete failed", status: "error" }); }
	};

	const handleDeleteTeamMember = async (item: ITeamMember) => {
		// TODO: Replace window.confirm with Chakra AlertDialog for better UX
		if (!window.confirm(`Delete team member "${item.name}"?`)) return;
		try {
			const ok = await adminApi.deleteTeamMember(getToken(), item.id);
			if (ok) { toast({ title: "Deleted", status: "success" }); cmsApi.getTeamMembers().then(setTeamMembers); }
			else { toast({ title: "Delete failed", status: "error" }); }
		} catch { toast({ title: "Delete failed", status: "error" }); }
	};

	const handleDeleteCaseStudy = async (item: ICaseStudy) => {
		// TODO: Replace window.confirm with Chakra AlertDialog for better UX
		if (!window.confirm(`Delete case study "${item.name}"?`)) return;
		try {
			const ok = await adminApi.deleteCaseStudy(getToken(), item.id);
			if (ok) { toast({ title: "Deleted", status: "success" }); cmsApi.getCaseStudies().then(setCaseStudies); }
			else { toast({ title: "Delete failed", status: "error" }); }
		} catch { toast({ title: "Delete failed", status: "error" }); }
	};

	const handleDeleteSiteSetting = async (item: ISiteSetting) => {
		// TODO: Replace window.confirm with Chakra AlertDialog for better UX
		if (!window.confirm(`Delete setting "${item.key}"?`)) return;
		try {
			const ok = await adminApi.deleteSiteSetting(getToken(), item.key);
			if (ok) { toast({ title: "Deleted", status: "success" }); cmsApi.getSiteSettings().then(setSiteSettings); }
			else { toast({ title: "Delete failed", status: "error" }); }
		} catch { toast({ title: "Delete failed", status: "error" }); }
	};

	// ---- Inline Create / Edit Handlers ----

	// Articles
	const [showAddArticle, setShowAddArticle] = useState(false);
	const [newArticle, setNewArticle] = useState({ title: "", url: "", description: "", type: "", source: "", typeColor: "blue" });
	const [articleLinks, setArticleLinks] = useState<Array<{ label: string; url: string }>>([]);
	const [editingArticle, setEditingArticle] = useState<IArticle | null>(null);
	const [sourceLogoFile, setSourceLogoFile] = useState<File | null>(null);
	const [isUploadingSourceLogo, setIsUploadingSourceLogo] = useState(false);
	const uploadingSourceLogoRef = useRef(false);
	const sourceLogoInputRef = useRef<HTMLInputElement>(null);
	const resetArticleForm = () => { setNewArticle({ title: "", url: "", description: "", type: "", source: "", typeColor: "blue" }); setArticleLinks([{ label: "Read Article", url: "" }]); setEditingArticle(null); setShowAddArticle(false); setSourceLogoFile(null); if (sourceLogoInputRef.current) sourceLogoInputRef.current.value = ""; };
	const handleEditArticle = (item: IArticle) => {
		setSourceLogoFile(null); if (sourceLogoInputRef.current) sourceLogoInputRef.current.value = "";
		setNewArticle({ title: item.title, url: item.url, description: item.description, type: item.type || "", source: item.source || "", typeColor: item.typeColor || "blue" });
		const links = (item.links || []).map((l) => ({ label: l.label, url: l.url }));
		setArticleLinks(links.length ? links : [{ label: "Read Article", url: item.url || "" }]);
		setEditingArticle(item); setShowAddArticle(true);
	};
	const handleSourceLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files?.length) return;
		const f = e.target.files[0];
		if (!validateImageFile(f, toast)) { e.target.value = ""; setSourceLogoFile(null); return; }
		setSourceLogoFile(f);
	};
	const handleUploadSourceLogo = async () => {
		if (!editingArticle || !sourceLogoFile || uploadingSourceLogoRef.current) return;
		uploadingSourceLogoRef.current = true;
		setIsUploadingSourceLogo(true);
		const targetId = editingArticle.id;
		try {
			const result = await adminApi.uploadImage<IArticle>(getToken(), `api/v1/article/${targetId}/logo`, sourceLogoFile);
			if (result) {
				toast({ title: "Source logo uploaded", status: "success" });
				setSourceLogoFile(null);
				if (sourceLogoInputRef.current) sourceLogoInputRef.current.value = "";
				setEditingArticle(current => current?.id === targetId ? result : current);
				cmsApi.getArticles(100).then(setArticles).catch(() => toast({ title: "Refresh failed", description: "Click Refresh to update.", status: "warning" }));
			} else {
				toast({ title: "Upload failed", description: "Please try again.", status: "error" });
			}
		} finally {
			uploadingSourceLogoRef.current = false;
			setIsUploadingSourceLogo(false);
		}
	};
	const handleSaveArticle = async () => {
		const validLinks = articleLinks.filter((l) => l.url.trim());
		const linksPayload = validLinks.map((l) => ({ id: 0, articleId: 0, label: l.label || "Read Article", url: l.url })) as IArticleLink[];
		const articleUrl = validLinks[0]?.url || "";
		try {
			if (editingArticle) {
				const result = await adminApi.updateArticle(getToken(), { ...editingArticle, ...newArticle, url: articleUrl, links: linksPayload } as any);
				if (result) { toast({ title: "Article updated", status: "success" }); resetArticleForm(); cmsApi.getArticles(100).then(setArticles); }
				else { toast({ title: "Failed to update", status: "error" }); }
			} else {
				const result = await adminApi.createArticle(getToken(), { ...newArticle, url: articleUrl, id: 0, links: linksPayload, sortOrder: 0 } as any);
				if (result) { toast({ title: "Article created", status: "success" }); resetArticleForm(); cmsApi.getArticles(100).then(setArticles); }
				else { toast({ title: "Failed to create", status: "error" }); }
			}
		} catch { toast({ title: editingArticle ? "Failed to update" : "Failed to create", status: "error" }); }
	};

	// Testimonials
	const [showAddTestimonial, setShowAddTestimonial] = useState(false);
	const [newTestimonial, setNewTestimonial] = useState({ name: "", location: "", quote: "" });
	const [editingTestimonial, setEditingTestimonial] = useState<ITestimonial | null>(null);
	const [headshotFile, setHeadshotFile] = useState<File | null>(null);
	const [isUploadingHeadshot, setIsUploadingHeadshot] = useState(false);
	const uploadingHeadshotRef = useRef(false);
	const headshotInputRef = useRef<HTMLInputElement>(null);
	const resetTestimonialForm = () => { setNewTestimonial({ name: "", location: "", quote: "" }); setEditingTestimonial(null); setShowAddTestimonial(false); setHeadshotFile(null); if (headshotInputRef.current) headshotInputRef.current.value = ""; };
	const handleEditTestimonial = (item: ITestimonial) => {
		setHeadshotFile(null); if (headshotInputRef.current) headshotInputRef.current.value = "";
		setNewTestimonial({ name: item.name, location: item.location || "", quote: item.quote });
		setEditingTestimonial(item); setShowAddTestimonial(true);
	};
	const handleHeadshotFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files?.length) return;
		const f = e.target.files[0];
		if (!validateImageFile(f, toast)) { e.target.value = ""; setHeadshotFile(null); return; }
		setHeadshotFile(f);
	};
	const handleSaveTestimonial = async () => {
		try {
			if (editingTestimonial) {
				const payload: ITestimonial = { ...editingTestimonial, name: newTestimonial.name, location: newTestimonial.location || undefined, quote: newTestimonial.quote };
				const result = await adminApi.updateTestimonial(getToken(), payload);
				if (result) { toast({ title: "Testimonial updated", status: "success" }); resetTestimonialForm(); cmsApi.getTestimonials().then(setTestimonials).catch(() => toast({ title: "Refresh failed", description: "Click Refresh to update.", status: "warning" })); }
				else { toast({ title: "Failed to update", status: "error" }); }
			} else {
				const payload: ITestimonial = { ...newTestimonial, location: newTestimonial.location || undefined, id: 0, sortOrder: 0, isActive: true };
				const result = await adminApi.createTestimonial(getToken(), payload);
				if (result) { toast({ title: "Testimonial created", status: "success" }); resetTestimonialForm(); cmsApi.getTestimonials().then(setTestimonials).catch(() => toast({ title: "Refresh failed", description: "Click Refresh to update.", status: "warning" })); }
				else { toast({ title: "Failed to create", status: "error" }); }
			}
		} catch { toast({ title: editingTestimonial ? "Failed to update" : "Failed to create", status: "error" }); }
	};
	const handleUploadHeadshot = async () => {
		if (!editingTestimonial || !headshotFile || uploadingHeadshotRef.current) return;
		uploadingHeadshotRef.current = true;
		setIsUploadingHeadshot(true);
		const targetId = editingTestimonial.id;
		try {
			const result = await adminApi.uploadImage<ITestimonial>(getToken(), `api/v1/testimonial/${targetId}/headshot`, headshotFile);
			if (result) {
				toast({ title: "Headshot uploaded", status: "success" });
				setHeadshotFile(null);
				if (headshotInputRef.current) headshotInputRef.current.value = "";
				setEditingTestimonial(current => current?.id === targetId ? result : current);
				cmsApi.getTestimonials().then(setTestimonials).catch(() => toast({ title: "Refresh failed", description: "Click Refresh to update.", status: "warning" }));
			} else {
				toast({ title: "Upload failed", description: "Please try again.", status: "error" });
			}
		} finally {
			uploadingHeadshotRef.current = false;
			setIsUploadingHeadshot(false);
		}
	};
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
	const handleTestimonialDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = testimonials.findIndex((t) => t.id === active.id);
		const newIndex = testimonials.findIndex((t) => t.id === over.id);
		const reordered = arrayMove(testimonials, oldIndex, newIndex).map((t, i) => ({ ...t, sortOrder: i }));
		setTestimonials(reordered);
		try {
			await Promise.all(reordered.map((t) => adminApi.updateTestimonial(getToken(), t)));
			toast({ title: "Order saved", status: "success", duration: 1500 });
		} catch { toast({ title: "Failed to save order", status: "error" }); }
	};
	const handleArticleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = articles.findIndex((t) => t.id === active.id);
		const newIndex = articles.findIndex((t) => t.id === over.id);
		const reordered = arrayMove(articles, oldIndex, newIndex).map((t, i) => ({ ...t, sortOrder: i }));
		setArticles(reordered);
		try {
			await Promise.all(reordered.map((t) => adminApi.updateArticle(getToken(), t)));
			toast({ title: "Order saved", status: "success", duration: 1500 });
		} catch { toast({ title: "Failed to save order", status: "error" }); }
	};
	const handlePricingDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = pricingTiers.findIndex((t) => t.id === active.id);
		const newIndex = pricingTiers.findIndex((t) => t.id === over.id);
		const reordered = arrayMove(pricingTiers, oldIndex, newIndex).map((t, i) => ({ ...t, sortOrder: i }));
		setPricingTiers(reordered);
		try {
			await Promise.all(reordered.map((t) => adminApi.updatePricingTier(getToken(), t)));
			toast({ title: "Order saved", status: "success", duration: 1500 });
		} catch { toast({ title: "Failed to save order", status: "error" }); }
	};
	const handlePartnerDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = partners.findIndex((t) => t.id === active.id);
		const newIndex = partners.findIndex((t) => t.id === over.id);
		const reordered = arrayMove(partners, oldIndex, newIndex).map((t, i) => ({ ...t, sortOrder: i }));
		setPartners(reordered);
		try {
			await Promise.all(reordered.map((t) => adminApi.updatePartner(getToken(), t)));
			toast({ title: "Order saved", status: "success", duration: 1500 });
		} catch { toast({ title: "Failed to save order", status: "error" }); }
	};
	const handleTeamDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = teamMembers.findIndex((t) => t.id === active.id);
		const newIndex = teamMembers.findIndex((t) => t.id === over.id);
		const reordered = arrayMove(teamMembers, oldIndex, newIndex).map((t, i) => ({ ...t, sortOrder: i }));
		setTeamMembers(reordered);
		try {
			await Promise.all(reordered.map((t) => adminApi.updateTeamMember(getToken(), t)));
			toast({ title: "Order saved", status: "success", duration: 1500 });
		} catch { toast({ title: "Failed to save order", status: "error" }); }
	};
	const handleCaseStudyDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = caseStudies.findIndex((t) => t.id === active.id);
		const newIndex = caseStudies.findIndex((t) => t.id === over.id);
		const reordered = arrayMove(caseStudies, oldIndex, newIndex).map((t, i) => ({ ...t, sortOrder: i }));
		setCaseStudies(reordered);
		try {
			await Promise.all(reordered.map((t) => adminApi.updateCaseStudy(getToken(), t)));
			toast({ title: "Order saved", status: "success", duration: 1500 });
		} catch { toast({ title: "Failed to save order", status: "error" }); }
	};

	// Pricing
	const [showAddPricing, setShowAddPricing] = useState(false);
	const [newPricing, setNewPricing] = useState({ title: "", price: "", period: "", features: "", commitment: "", isHighlighted: false });
	const [editingPricing, setEditingPricing] = useState<IPricingTier | null>(null);
	const resetPricingForm = () => { setNewPricing({ title: "", price: "", period: "", features: "", commitment: "", isHighlighted: false }); setEditingPricing(null); setShowAddPricing(false); };
	const handleEditPricing = (item: IPricingTier) => {
		setNewPricing({ title: item.title, price: item.price, period: item.period || "", features: item.features || "", commitment: item.commitment || "", isHighlighted: item.isHighlighted });
		setEditingPricing(item); setShowAddPricing(true);
	};
	const handleSavePricing = async () => {
		try {
			if (editingPricing) {
				const result = await adminApi.updatePricingTier(getToken(), { ...editingPricing, ...newPricing } as any);
				if (result) { toast({ title: "Pricing tier updated", status: "success" }); resetPricingForm(); cmsApi.getPricingTiers().then(setPricingTiers); }
				else { toast({ title: "Failed to update", status: "error" }); }
			} else {
				const result = await adminApi.createPricingTier(getToken(), { ...newPricing, id: 0, sortOrder: 0 } as any);
				if (result) { toast({ title: "Pricing tier created", status: "success" }); resetPricingForm(); cmsApi.getPricingTiers().then(setPricingTiers); }
				else { toast({ title: "Failed to create", status: "error" }); }
			}
		} catch { toast({ title: editingPricing ? "Failed to update" : "Failed to create", status: "error" }); }
	};

	// Partners
	const [showAddPartner, setShowAddPartner] = useState(false);
	const [newPartner, setNewPartner] = useState({ name: "", websiteUrl: "" });
	const [editingPartner, setEditingPartner] = useState<IPartner | null>(null);
	const [logoFile, setLogoFile] = useState<File | null>(null);
	const [isUploadingLogo, setIsUploadingLogo] = useState(false);
	const uploadingLogoRef = useRef(false);
	const logoInputRef = useRef<HTMLInputElement>(null);
	const resetPartnerForm = () => { setNewPartner({ name: "", websiteUrl: "" }); setEditingPartner(null); setShowAddPartner(false); setLogoFile(null); if (logoInputRef.current) logoInputRef.current.value = ""; };
	const handleEditPartner = (item: IPartner) => {
		setLogoFile(null); if (logoInputRef.current) logoInputRef.current.value = "";
		setNewPartner({ name: item.name, websiteUrl: item.websiteUrl || "" });
		setEditingPartner(item); setShowAddPartner(true);
	};
	const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files?.length) return;
		const f = e.target.files[0];
		if (!validateImageFile(f, toast)) { e.target.value = ""; setLogoFile(null); return; }
		setLogoFile(f);
	};
	const handleUploadLogo = async () => {
		if (!editingPartner || !logoFile || uploadingLogoRef.current) return;
		uploadingLogoRef.current = true;
		setIsUploadingLogo(true);
		const targetId = editingPartner.id;
		try {
			const result = await adminApi.uploadImage<IPartner>(getToken(), `api/v1/partner/${targetId}/logo`, logoFile);
			if (result) {
				toast({ title: "Logo uploaded", status: "success" });
				setLogoFile(null);
				if (logoInputRef.current) logoInputRef.current.value = "";
				setEditingPartner(current => current?.id === targetId ? result : current);
				cmsApi.getPartners().then(setPartners).catch(() => toast({ title: "Refresh failed", description: "Click Refresh to update.", status: "warning" }));
			} else {
				toast({ title: "Upload failed", description: "Please try again.", status: "error" });
			}
		} finally {
			uploadingLogoRef.current = false;
			setIsUploadingLogo(false);
		}
	};
	const handleSavePartner = async () => {
		if (newPartner.websiteUrl && !isHttpUrl(newPartner.websiteUrl)) {
			toast({ title: "Invalid website URL", description: "URL must start with http:// or https://", status: "error" }); return;
		}
		try {
			if (editingPartner) {
				const payload: IPartner = { ...editingPartner, name: newPartner.name, websiteUrl: newPartner.websiteUrl || undefined, isActive: editingPartner.isActive };
				const result = await adminApi.updatePartner(getToken(), payload);
				if (result) { toast({ title: "Partner updated", status: "success" }); resetPartnerForm(); cmsApi.getPartners().then(setPartners).catch(() => toast({ title: "Refresh failed", description: "Click Refresh to update.", status: "warning" })); }
				else { toast({ title: "Failed to update", status: "error" }); }
			} else {
				const payload: IPartner = { name: newPartner.name, websiteUrl: newPartner.websiteUrl || undefined, id: 0, sortOrder: 0, isActive: true };
				const result = await adminApi.createPartner(getToken(), payload);
				if (result) { toast({ title: "Partner created", status: "success" }); resetPartnerForm(); cmsApi.getPartners().then(setPartners).catch(() => toast({ title: "Refresh failed", description: "Click Refresh to update.", status: "warning" })); }
				else { toast({ title: "Failed to create", status: "error" }); }
			}
		} catch { toast({ title: editingPartner ? "Failed to update" : "Failed to create", status: "error" }); }
	};

	// Team Members
	const [showAddTeamMember, setShowAddTeamMember] = useState(false);
	const [newTeamMember, setNewTeamMember] = useState({ name: "", role: "", email: "", highlights: "", phone: "", linkedInUrl: "" });
	const [editingTeamMember, setEditingTeamMember] = useState<ITeamMember | null>(null);
	const [avatarFile, setAvatarFile] = useState<File | null>(null);
	const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
	const uploadingAvatarRef = useRef(false);
	const avatarInputRef = useRef<HTMLInputElement>(null);
	const resetTeamMemberForm = () => { setNewTeamMember({ name: "", role: "", email: "", highlights: "", phone: "", linkedInUrl: "" }); setEditingTeamMember(null); setShowAddTeamMember(false); setAvatarFile(null); if (avatarInputRef.current) avatarInputRef.current.value = ""; };
	const handleEditTeamMember = (item: ITeamMember) => {
		setAvatarFile(null); if (avatarInputRef.current) avatarInputRef.current.value = "";
		setNewTeamMember({ name: item.name, role: item.role || "", email: item.email || "", highlights: item.highlights || "", phone: item.phone || "", linkedInUrl: item.linkedInUrl || "" });
		setEditingTeamMember(item); setShowAddTeamMember(true);
	};
	const handleAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files?.length) return;
		const f = e.target.files[0];
		if (!validateImageFile(f, toast)) { e.target.value = ""; setAvatarFile(null); return; }
		setAvatarFile(f);
	};
	const handleUploadAvatar = async () => {
		if (!editingTeamMember || !avatarFile || uploadingAvatarRef.current) return;
		uploadingAvatarRef.current = true;
		setIsUploadingAvatar(true);
		const targetId = editingTeamMember.id;
		try {
			const result = await adminApi.uploadImage<ITeamMember>(getToken(), `api/v1/teammember/${targetId}/avatar`, avatarFile);
			if (result) {
				toast({ title: "Profile picture uploaded", status: "success" });
				setAvatarFile(null);
				if (avatarInputRef.current) avatarInputRef.current.value = "";
				setEditingTeamMember(current => current?.id === targetId ? result : current);
				cmsApi.getTeamMembers().then(setTeamMembers).catch(() => toast({ title: "Refresh failed", description: "Click Refresh to update.", status: "warning" }));
			} else {
				toast({ title: "Upload failed", description: "Please try again.", status: "error" });
			}
		} finally {
			uploadingAvatarRef.current = false;
			setIsUploadingAvatar(false);
		}
	};
	const handleSaveTeamMember = async () => {
		if (newTeamMember.linkedInUrl && !isHttpUrl(newTeamMember.linkedInUrl)) {
			toast({ title: "Invalid LinkedIn URL", description: "URL must start with http:// or https://", status: "error" });
			return;
		}
		try {
			if (editingTeamMember) {
				const payload: ITeamMember = { ...editingTeamMember, name: newTeamMember.name, role: newTeamMember.role || undefined, email: newTeamMember.email || undefined, highlights: newTeamMember.highlights || undefined, phone: newTeamMember.phone || undefined, linkedInUrl: newTeamMember.linkedInUrl || undefined, isActive: editingTeamMember.isActive };
				const result = await adminApi.updateTeamMember(getToken(), payload);
				if (result) { toast({ title: "Team member updated", status: "success" }); resetTeamMemberForm(); cmsApi.getTeamMembers().then(setTeamMembers).catch(() => toast({ title: "Refresh failed", description: "Click Refresh to update.", status: "warning" })); }
				else { toast({ title: "Failed to update", status: "error" }); }
			} else {
				const payload: ITeamMember = { name: newTeamMember.name, role: newTeamMember.role || undefined, email: newTeamMember.email || undefined, highlights: newTeamMember.highlights || undefined, phone: newTeamMember.phone || undefined, linkedInUrl: newTeamMember.linkedInUrl || undefined, id: 0, sortOrder: 0, isActive: true };
				const result = await adminApi.createTeamMember(getToken(), payload);
				if (result) { toast({ title: "Team member created", status: "success" }); resetTeamMemberForm(); cmsApi.getTeamMembers().then(setTeamMembers).catch(() => toast({ title: "Refresh failed", description: "Click Refresh to update.", status: "warning" })); }
				else { toast({ title: "Failed to create", status: "error" }); }
			}
		} catch { toast({ title: editingTeamMember ? "Failed to update" : "Failed to create", status: "error" }); }
	};

	// Case Studies
	const [showAddCaseStudy, setShowAddCaseStudy] = useState(false);
	const [newCaseStudy, setNewCaseStudy] = useState({ name: "", region: "", problem: "", action: "", tags: "" });
	const [editingCaseStudy, setEditingCaseStudy] = useState<ICaseStudy | null>(null);
	const [caseStudyImages, setCaseStudyImages] = useState<ICaseStudyImage[]>([]);
	const [csImageFile, setCsImageFile] = useState<File | null>(null);
	const [isUploadingCsImage, setIsUploadingCsImage] = useState(false);
	const uploadingCsImageRef = useRef(false);
	const csImageInputRef = useRef<HTMLInputElement>(null);
	const [newCsImageMeta, setNewCsImageMeta] = useState({ imageType: "weedHistory", label: "", altText: "" });
	const resetCaseStudyForm = () => { setNewCaseStudy({ name: "", region: "", problem: "", action: "", tags: "" }); setEditingCaseStudy(null); setShowAddCaseStudy(false); setCaseStudyImages([]); setCsImageFile(null); setNewCsImageMeta({ imageType: "weedHistory", label: "", altText: "" }); if (csImageInputRef.current) csImageInputRef.current.value = ""; };
	const handleEditCaseStudy = (item: ICaseStudy) => {
		setNewCaseStudy({ name: item.name, region: item.region || "", problem: item.problem || "", action: item.action || "", tags: item.tags || "" });
		setCaseStudyImages(item.images ? [...item.images] : []);
		setCsImageFile(null); setNewCsImageMeta({ imageType: "weedHistory", label: "", altText: "" }); if (csImageInputRef.current) csImageInputRef.current.value = "";
		setEditingCaseStudy(item); setShowAddCaseStudy(true);
	};
	const handleCsImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files?.length) return;
		const f = e.target.files[0];
		if (!validateImageFile(f, toast)) { e.target.value = ""; setCsImageFile(null); return; }
		setCsImageFile(f);
	};
	const handleCsImageUpload = async () => {
		if (!editingCaseStudy || !csImageFile || uploadingCsImageRef.current) return;
		uploadingCsImageRef.current = true;
		setIsUploadingCsImage(true);
		try {
			const result = await adminApi.uploadImage<{ imageUrl: string; imageType: string; label: string }>(
				getToken(),
				`api/v1/casestudy/${editingCaseStudy.id}/image?imageType=${encodeURIComponent(newCsImageMeta.imageType)}&label=${encodeURIComponent(newCsImageMeta.label)}`,
				csImageFile
			);
			if (result) {
				const newImage: ICaseStudyImage = {
					id: 0, caseStudyId: editingCaseStudy.id,
					imageType: newCsImageMeta.imageType, label: newCsImageMeta.label,
					imageUrl: result.imageUrl, altText: newCsImageMeta.altText,
					sortOrder: caseStudyImages.length,
				};
				setCaseStudyImages(prev => [...prev, newImage]);
				toast({ title: "Image uploaded", description: "Click Update to save changes.", status: "success" });
				setCsImageFile(null); setNewCsImageMeta({ imageType: "weedHistory", label: "", altText: "" });
				if (csImageInputRef.current) csImageInputRef.current.value = "";
			} else {
				toast({ title: "Upload failed", status: "error" });
			}
		} finally { uploadingCsImageRef.current = false; setIsUploadingCsImage(false); }
	};
	const handleSaveCaseStudy = async () => {
		try {
			if (editingCaseStudy) {
				const images = caseStudyImages.map((img, i) => ({ ...img, sortOrder: i }));
				const result = await adminApi.updateCaseStudy(getToken(), { ...editingCaseStudy, ...newCaseStudy, images } as any);
				if (result) { toast({ title: "Case study updated", status: "success" }); resetCaseStudyForm(); cmsApi.getCaseStudies().then(setCaseStudies); }
				else { toast({ title: "Failed to update", status: "error" }); }
			} else {
				const result = await adminApi.createCaseStudy(getToken(), { ...newCaseStudy, id: 0, sortOrder: 0, images: [] } as any);
				if (result) { toast({ title: "Case study created", status: "success" }); resetCaseStudyForm(); cmsApi.getCaseStudies().then(setCaseStudies); }
				else { toast({ title: "Failed to create", status: "error" }); }
			}
		} catch { toast({ title: editingCaseStudy ? "Failed to update" : "Failed to create", status: "error" }); }
	};

	// Site Settings
	const [showAddSetting, setShowAddSetting] = useState(false);
	const [newSetting, setNewSetting] = useState({ key: "", value: "", description: "" });
	const [editingSetting, setEditingSetting] = useState<ISiteSetting | null>(null);
	const resetSettingForm = () => { setNewSetting({ key: "", value: "", description: "" }); setEditingSetting(null); setShowAddSetting(false); };

	// Hero Background Image Upload
	const [heroImageFile, setHeroImageFile] = useState<File | null>(null);
	const [isUploadingHeroImage, setIsUploadingHeroImage] = useState(false);
	const uploadingHeroImageRef = useRef(false);
	const heroImageInputRef = useRef<HTMLInputElement>(null);
	const handleHeroImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files?.length) return;
		const f = e.target.files[0];
		if (!validateImageFile(f, toast)) { e.target.value = ""; setHeroImageFile(null); return; }
		setHeroImageFile(f);
	};
	const handleUploadHeroImage = async () => {
		if (!heroImageFile || uploadingHeroImageRef.current) return;
		uploadingHeroImageRef.current = true;
		setIsUploadingHeroImage(true);
		try {
			const result = await adminApi.uploadImage<ISiteSetting>(getToken(), "api/v1/sitesettings/heroBackgroundImage/image", heroImageFile);
			if (result) {
				toast({ title: "Hero image uploaded", status: "success" });
				setHeroImageFile(null);
				if (heroImageInputRef.current) heroImageInputRef.current.value = "";
				cmsApi.getSiteSettings().then(setSiteSettings).catch(() => toast({ title: "Refresh failed", description: "Click Refresh to update.", status: "warning" }));
			} else {
				toast({ title: "Upload failed", description: "Please try again.", status: "error" });
			}
		} finally {
			uploadingHeroImageRef.current = false;
			setIsUploadingHeroImage(false);
		}
	};
	const handleEditSetting = (item: ISiteSetting) => {
		setNewSetting({ key: item.key, value: item.value, description: item.description || "" });
		setEditingSetting(item); setShowAddSetting(true);
	};
	const handleSaveSetting = async () => {
		try {
			const result = await adminApi.upsertSiteSetting(getToken(), newSetting as ISiteSetting);
			if (result) { toast({ title: "Setting saved", status: "success" }); resetSettingForm(); cmsApi.getSiteSettings().then(setSiteSettings); }
			else { toast({ title: "Failed to save", status: "error" }); }
		} catch { toast({ title: "Failed to save", status: "error" }); }
	};

	// Admin Users
	const [showAddUser, setShowAddUser] = useState(false);
	const [newUserEmail, setNewUserEmail] = useState("");
	const handleAddUser = async () => {
		try {
			const result = await adminApi.addUser(getToken(), newUserEmail);
			if (result) { toast({ title: "User added", status: "success" }); setNewUserEmail(""); setShowAddUser(false); adminApi.getUsers(getToken()).then(setCmsUsers); }
			else { toast({ title: "Failed to add user", status: "error" }); }
		} catch { toast({ title: "Failed to add user", status: "error" }); }
	};
	const handleDeleteUser = async (user: ICmsUser) => {
		if (!window.confirm(`Remove user "${user.email}" from CMS access?`)) return;
		try {
			const ok = await adminApi.deleteUser(getToken(), user.id);
			if (ok) { toast({ title: "User removed", status: "success" }); adminApi.getUsers(getToken()).then(setCmsUsers); }
			else { toast({ title: "Failed to remove user", status: "error" }); }
		} catch { toast({ title: "Failed to remove user", status: "error" }); }
	};


	return (
		<Box minH="100vh" bg="gray.50">
			<Box bg="white" borderBottom="1px solid" borderColor="gray.200" py="3">
				<Container maxW="1400px">
					<Stack direction="row" justify="space-between" align="center">
						<Heading size="md" color="green.700">Geco CMS</Heading>
						<Stack direction="row" align="center" spacing="4">
							<Text fontSize="sm" color="gray.600">{email}</Text>
							<Button size="sm" leftIcon={<MdLogout />} variant="outline" onClick={handleLogout}>
								Logout
							</Button>
						</Stack>
					</Stack>
				</Container>
			</Box>

			<Container maxW="1400px" py="8">
				{isLoadingData && (
					<Center py="12">
						<Spinner size="xl" color="green.500" />
					</Center>
				)}
				{loadError && !isLoadingData && (
					<Text color="red.500" mb="4">{loadError}</Text>
				)}
				{!isLoadingData && (
				<Tabs variant="enclosed" colorScheme="green">
					<TabList flexWrap="wrap">
						<Tab>Articles</Tab>
						<Tab>Testimonials</Tab>
						<Tab>Pricing</Tab>
						<Tab>Partners</Tab>
						<Tab>Team</Tab>
						<Tab>Case Studies</Tab>
						<Tab>Settings</Tab>
						<Tab>Admin Users</Tab>
					</TabList>
					<TabPanels>
						<TabPanel>
							<Stack spacing="4">
								<Stack direction="row" justify="space-between" align="center">
									<Heading size="md">Articles</Heading>
									<Button size="sm" onClick={() => cmsApi.getArticles(100).then(setArticles)}>Refresh</Button>
								</Stack>
								<Text fontSize="xs" color="gray.500">Drag rows to reorder.</Text>
								<Box overflowX="auto">
									<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleArticleDragEnd}>
										<Table size="sm">
											<Thead><Tr><Th width="40px"></Th><Th>ID</Th><Th>Title</Th><Th>Type</Th><Th>Source</Th><Th>Actions</Th></Tr></Thead>
											<Tbody>
												<SortableContext items={articles.map((t) => t.id)} strategy={verticalListSortingStrategy}>
													{articles.map((item) => (
														<SortableRow key={item.id} item={item} columns={[{ key: "id" }, { key: "title", maxW: "300px" }, { key: "type" }, { key: "source" }]} onEdit={handleEditArticle} onDelete={handleDeleteArticle} />
													))}
												</SortableContext>
											</Tbody>
										</Table>
									</DndContext>
								</Box>
								{articles.length === 0 && <Text color="gray.500" textAlign="center" py="4">No articles yet.</Text>}
							</Stack>
							<Stack direction="row" mt="4" spacing="2">
								<Button size="sm" leftIcon={<MdAdd />} onClick={() => { resetArticleForm(); setShowAddArticle(!showAddArticle); }}>{editingArticle ? "Cancel Edit" : "Add New Article"}</Button>
								{editingArticle && <Button size="sm" variant="ghost" onClick={resetArticleForm}>Cancel</Button>}
							</Stack>
							<Collapse in={showAddArticle}>
								<Box p="4" mt="2" bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
									<Stack spacing="3">
										<Input placeholder="Title" size="sm" value={newArticle.title} onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })} />
										<Input placeholder="Type (e.g. News, Interview, Podcast, Video)" size="sm" value={newArticle.type} onChange={(e) => setNewArticle({ ...newArticle, type: e.target.value })} />
										<Input placeholder="Source (e.g. YouTube, Grainews)" size="sm" value={newArticle.source} onChange={(e) => setNewArticle({ ...newArticle, source: e.target.value })} />
										<Select size="sm" value={newArticle.typeColor} onChange={(e) => setNewArticle({ ...newArticle, typeColor: e.target.value })}>
											<option value="blue">Blue (Article)</option>
											<option value="green">Green (News)</option>
											<option value="purple">Purple (Podcast)</option>
											<option value="red">Red (Video)</option>
										</Select>
										<Textarea placeholder="Date or short description (e.g. November 2024)" size="sm" value={newArticle.description} onChange={(e) => setNewArticle({ ...newArticle, description: e.target.value })} />
										<Text fontSize="sm" fontWeight="bold" color="gray.600">Links (at least one required)</Text>
										{articleLinks.map((link, idx) => (
											<Stack key={idx} direction="row" spacing="2" align="center">
												<Input placeholder="Label (e.g. Read Article, Watch on YouTube)" size="sm" flex="1" value={link.label} onChange={(e) => { const updated = [...articleLinks]; updated[idx] = { ...updated[idx], label: e.target.value }; setArticleLinks(updated); }} />
												<Input placeholder="URL" size="sm" type="url" flex="2" value={link.url} onChange={(e) => { const updated = [...articleLinks]; updated[idx] = { ...updated[idx], url: e.target.value }; setArticleLinks(updated); }} />
												{articleLinks.length > 1 && <Button size="sm" variant="ghost" colorScheme="red" onClick={() => setArticleLinks(articleLinks.filter((_, i) => i !== idx))}>×</Button>}
											</Stack>
										))}
										<Button size="xs" variant="outline" leftIcon={<MdAdd />} onClick={() => setArticleLinks([...articleLinks, { label: "", url: "" }])}>Add Another Link</Button>
										<Button size="sm" colorScheme="green" onClick={handleSaveArticle} isDisabled={!newArticle.title || !articleLinks.some((l) => l.url.trim()) || isUploadingSourceLogo}>{editingArticle ? "Update" : "Create"}</Button>
										{editingArticle && (
											<Box p="3" bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
												<Text fontSize="sm" fontWeight="bold" color="gray.600" mb="2">Source Logo</Text>
												<Stack direction="row" align="center" spacing="3">
													{isHttpUrl(editingArticle.sourceLogoUrl) ? (
														<Image src={editingArticle.sourceLogoUrl} alt={editingArticle.source || "Source logo"} h="48px" maxW="96px" objectFit="contain" borderRadius="md" border="1px solid" borderColor="gray.200" bg="white" p="1" fallback={<Box w="48px" h="48px" bg="gray.200" borderRadius="md" display="flex" alignItems="center" justifyContent="center"><Text fontSize="xs" color="gray.500">No logo</Text></Box>} />
													) : (
														<Box w="48px" h="48px" bg="gray.200" borderRadius="md" display="flex" alignItems="center" justifyContent="center"><Text fontSize="xs" color="gray.500">No logo</Text></Box>
													)}
													<Stack spacing="1" flex="1">
														<input
															type="file"
															accept="image/jpeg,image/png,image/gif,image/webp"
															ref={sourceLogoInputRef}
															style={{ display: "none" }}
															onChange={handleSourceLogoFileChange}
														/>
														<Stack direction="row" align="center" spacing="2">
															<Button size="xs" variant="outline" leftIcon={<MdUpload />} onClick={() => sourceLogoInputRef.current?.click()}>
																{sourceLogoFile ? "Change File" : "Choose File"}
															</Button>
															{sourceLogoFile && (
																<>
																	<Text fontSize="xs" color="gray.500" isTruncated maxW="150px">{sourceLogoFile.name}</Text>
																	<Button size="xs" colorScheme="green" onClick={handleUploadSourceLogo} isLoading={isUploadingSourceLogo} isDisabled={isUploadingSourceLogo}>Upload</Button>
																</>
															)}
														</Stack>
														<Text fontSize="xs" color="gray.400">JPG, PNG, GIF, WebP. Max 5MB.</Text>
													</Stack>
												</Stack>
											</Box>
										)}
										{!editingArticle && (
											<Text fontSize="xs" color="gray.400">Save the article first to upload a source logo.</Text>
										)}
									</Stack>
								</Box>
							</Collapse>
						</TabPanel>
						<TabPanel>
							<Stack spacing="4">
								<Stack direction="row" justify="space-between" align="center">
									<Heading size="md">Testimonials</Heading>
									<Button size="sm" onClick={() => cmsApi.getTestimonials().then(setTestimonials)}>Refresh</Button>
								</Stack>
								<Text fontSize="xs" color="gray.500">Drag rows to reorder. Order is saved automatically.</Text>
								<Box overflowX="auto">
									<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleTestimonialDragEnd}>
										<Table size="sm">
											<Thead>
												<Tr>
													<Th width="40px"></Th>
													<Th>ID</Th>
													<Th>Name</Th>
													<Th>Location</Th>
													<Th>Quote</Th>
													<Th>Actions</Th>
												</Tr>
											</Thead>
											<Tbody>
												<SortableContext items={testimonials.map((t) => t.id)} strategy={verticalListSortingStrategy}>
													{testimonials.map((item) => (
														<SortableRow key={item.id} item={item} columns={[{ key: "id" }, { key: "name", maxW: "150px" }, { key: "location", maxW: "150px" }, { key: "quote", maxW: "300px" }]} onEdit={handleEditTestimonial} onDelete={handleDeleteTestimonial} />
													))}
												</SortableContext>
											</Tbody>
										</Table>
									</DndContext>
								</Box>
								{testimonials.length === 0 && (
									<Text color="gray.500" textAlign="center" py="4">No testimonials yet.</Text>
								)}
							</Stack>
							<Stack direction="row" mt="4" spacing="2">
								<Button size="sm" leftIcon={<MdAdd />} onClick={() => { resetTestimonialForm(); setShowAddTestimonial(!showAddTestimonial); }}>{editingTestimonial ? "Cancel Edit" : "Add New Testimonial"}</Button>
								{editingTestimonial && <Button size="sm" variant="ghost" onClick={resetTestimonialForm}>Cancel</Button>}
							</Stack>
							<Collapse in={showAddTestimonial}>
								<Box p="4" mt="2" bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
									<Stack spacing="3">
										<Input placeholder="Name" size="sm" value={newTestimonial.name} onChange={(e) => setNewTestimonial({ ...newTestimonial, name: e.target.value })} />
										<Input placeholder="Location" size="sm" value={newTestimonial.location} onChange={(e) => setNewTestimonial({ ...newTestimonial, location: e.target.value })} />
										<Textarea placeholder="Quote" size="sm" value={newTestimonial.quote} onChange={(e) => setNewTestimonial({ ...newTestimonial, quote: e.target.value })} />
										<Button size="sm" colorScheme="green" onClick={handleSaveTestimonial} isDisabled={!newTestimonial.name || !newTestimonial.quote || isUploadingHeadshot}>{editingTestimonial ? "Update" : "Create"}</Button>
										{editingTestimonial && (
											<Box p="3" bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
												<Text fontSize="sm" fontWeight="bold" color="gray.600" mb="2">Headshot</Text>
												<Stack direction="row" align="center" spacing="3">
													{isHttpUrl(editingTestimonial.headshotUrl) ? (
														<Image src={editingTestimonial.headshotUrl} alt={editingTestimonial.name} boxSize="48px" borderRadius="full" objectFit="cover" fallback={<Avatar name={editingTestimonial.name} size="md" bg="green.600" color="white" />} />
													) : (
														<Avatar name={editingTestimonial.name} size="md" bg="green.600" color="white" />
													)}
													<Stack spacing="1" flex="1">
														<input
															type="file"
															accept="image/jpeg,image/png,image/gif,image/webp"
															ref={headshotInputRef}
															style={{ display: "none" }}
															onChange={handleHeadshotFileChange}
														/>
														<Stack direction="row" align="center" spacing="2">
															<Button size="xs" variant="outline" leftIcon={<MdUpload />} onClick={() => headshotInputRef.current?.click()}>
																{headshotFile ? "Change File" : "Choose File"}
															</Button>
															{headshotFile && (
																<>
																	<Text fontSize="xs" color="gray.500" isTruncated maxW="150px">{headshotFile.name}</Text>
																	<Button size="xs" colorScheme="green" onClick={handleUploadHeadshot} isLoading={isUploadingHeadshot} isDisabled={isUploadingHeadshot}>Upload</Button>
																</>
															)}
														</Stack>
														<Text fontSize="xs" color="gray.400">JPG, PNG, GIF, WebP. Max 5MB.</Text>
													</Stack>
												</Stack>
											</Box>
										)}
										{!editingTestimonial && (
											<Text fontSize="xs" color="gray.400">Save the testimonial first to upload a headshot.</Text>
										)}
									</Stack>
								</Box>
							</Collapse>
						</TabPanel>
						<TabPanel>
							<Stack spacing="4">
								<Stack direction="row" justify="space-between" align="center">
									<Heading size="md">Pricing Tiers</Heading>
									<Button size="sm" onClick={() => cmsApi.getPricingTiers().then(setPricingTiers)}>Refresh</Button>
								</Stack>
								<Text fontSize="xs" color="gray.500">Drag rows to reorder.</Text>
								<Box overflowX="auto">
									<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePricingDragEnd}>
										<Table size="sm">
											<Thead><Tr><Th width="40px"></Th><Th>ID</Th><Th>Title</Th><Th>Price</Th><Th>Period</Th><Th>Highlighted</Th><Th>Actions</Th></Tr></Thead>
											<Tbody>
												<SortableContext items={pricingTiers.map((t) => t.id)} strategy={verticalListSortingStrategy}>
													{pricingTiers.map((item) => (
														<SortableRow key={item.id} item={item} columns={[{ key: "id" }, { key: "title" }, { key: "price" }, { key: "period" }, { key: "isHighlighted" }]} onEdit={handleEditPricing} onDelete={handleDeletePricingTier} />
													))}
												</SortableContext>
											</Tbody>
										</Table>
									</DndContext>
								</Box>
								{pricingTiers.length === 0 && <Text color="gray.500" textAlign="center" py="4">No pricing tiers yet.</Text>}
							</Stack>
							<Stack direction="row" mt="4" spacing="2">
								<Button size="sm" leftIcon={<MdAdd />} onClick={() => { resetPricingForm(); setShowAddPricing(!showAddPricing); }}>{editingPricing ? "Cancel Edit" : "Add New Pricing Tier"}</Button>
								{editingPricing && <Button size="sm" variant="ghost" onClick={resetPricingForm}>Cancel</Button>}
							</Stack>
							<Collapse in={showAddPricing}>
								<Box p="4" mt="2" bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
									<Stack spacing="3">
										<Input placeholder="Title" size="sm" value={newPricing.title} onChange={(e) => setNewPricing({ ...newPricing, title: e.target.value })} />
										<Input placeholder="Price (e.g. $5/ac)" size="sm" value={newPricing.price} onChange={(e) => setNewPricing({ ...newPricing, price: e.target.value })} />
										<Input placeholder="Period (e.g. per year)" size="sm" value={newPricing.period} onChange={(e) => setNewPricing({ ...newPricing, period: e.target.value })} />
										<Textarea placeholder="Features (one per line)" size="sm" value={newPricing.features} onChange={(e) => setNewPricing({ ...newPricing, features: e.target.value })} />
										<Input placeholder="Commitment (e.g. 1 year)" size="sm" value={newPricing.commitment} onChange={(e) => setNewPricing({ ...newPricing, commitment: e.target.value })} />
										<Checkbox isChecked={newPricing.isHighlighted} onChange={(e) => setNewPricing({ ...newPricing, isHighlighted: e.target.checked })}>Highlighted</Checkbox>
										<Button size="sm" colorScheme="green" onClick={handleSavePricing} isDisabled={!newPricing.title || !newPricing.price}>{editingPricing ? "Update" : "Create"}</Button>
									</Stack>
								</Box>
							</Collapse>
						</TabPanel>
						<TabPanel>
							<Stack spacing="4">
								<Stack direction="row" justify="space-between" align="center">
									<Heading size="md">Partners</Heading>
									<Button size="sm" onClick={() => cmsApi.getPartners().then(setPartners)}>Refresh</Button>
								</Stack>
								<Text fontSize="xs" color="gray.500">Drag rows to reorder.</Text>
								<Box overflowX="auto">
									<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePartnerDragEnd}>
										<Table size="sm">
											<Thead><Tr><Th width="40px"></Th><Th>ID</Th><Th>Name</Th><Th>Logo URL</Th><Th>Website</Th><Th>Actions</Th></Tr></Thead>
											<Tbody>
												<SortableContext items={partners.map((t) => t.id)} strategy={verticalListSortingStrategy}>
													{partners.map((item) => (
														<SortableRow key={item.id} item={item} columns={[{ key: "id" }, { key: "name" }, { key: "logoUrl" }, { key: "websiteUrl" }]} onEdit={handleEditPartner} onDelete={handleDeletePartner} />
													))}
												</SortableContext>
											</Tbody>
										</Table>
									</DndContext>
								</Box>
								{partners.length === 0 && <Text color="gray.500" textAlign="center" py="4">No partners yet.</Text>}
							</Stack>
							<Stack direction="row" mt="4" spacing="2">
								<Button size="sm" leftIcon={<MdAdd />} onClick={() => { resetPartnerForm(); setShowAddPartner(!showAddPartner); }}>{editingPartner ? "Cancel Edit" : "Add New Partner"}</Button>
								{editingPartner && <Button size="sm" variant="ghost" onClick={resetPartnerForm}>Cancel</Button>}
							</Stack>
							<Collapse in={showAddPartner}>
								<Box p="4" mt="2" bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
									<Stack spacing="3">
										<Input placeholder="Name" size="sm" value={newPartner.name} onChange={(e) => setNewPartner({ ...newPartner, name: e.target.value })} />
										<Input placeholder="Website URL (https://...)" size="sm" value={newPartner.websiteUrl} onChange={(e) => setNewPartner({ ...newPartner, websiteUrl: e.target.value })} />
										<Button size="sm" colorScheme="green" onClick={handleSavePartner} isDisabled={!newPartner.name || isUploadingLogo}>{editingPartner ? "Update" : "Create"}</Button>
										{editingPartner && (
											<Box p="3" bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
												<Text fontSize="sm" fontWeight="bold" color="gray.600" mb="2">Logo</Text>
												<Stack direction="row" align="center" spacing="3">
													{isHttpUrl(editingPartner.logoUrl) ? (
														<Image src={editingPartner.logoUrl} alt={editingPartner.name} h="48px" maxW="96px" objectFit="contain" borderRadius="md" border="1px solid" borderColor="gray.200" bg="white" p="1" fallback={<Box w="48px" h="48px" bg="gray.200" borderRadius="md" display="flex" alignItems="center" justifyContent="center"><Text fontSize="xs" color="gray.500">No logo</Text></Box>} />
													) : (
														<Box w="48px" h="48px" bg="gray.200" borderRadius="md" display="flex" alignItems="center" justifyContent="center"><Text fontSize="xs" color="gray.500">No logo</Text></Box>
													)}
													<Stack spacing="1" flex="1">
														<input
															type="file"
															accept="image/jpeg,image/png,image/gif,image/webp"
															ref={logoInputRef}
															style={{ display: "none" }}
															onChange={handleLogoFileChange}
														/>
														<Stack direction="row" align="center" spacing="2">
															<Button size="xs" variant="outline" leftIcon={<MdUpload />} onClick={() => logoInputRef.current?.click()}>
																{logoFile ? "Change File" : "Choose File"}
															</Button>
															{logoFile && (
																<>
																	<Text fontSize="xs" color="gray.500" isTruncated maxW="150px">{logoFile.name}</Text>
																	<Button size="xs" colorScheme="green" onClick={handleUploadLogo} isLoading={isUploadingLogo} isDisabled={isUploadingLogo}>Upload</Button>
																</>
															)}
														</Stack>
														<Text fontSize="xs" color="gray.400">JPG, PNG, GIF, WebP. Max 5MB.</Text>
													</Stack>
												</Stack>
											</Box>
										)}
										{!editingPartner && (
											<Text fontSize="xs" color="gray.400">Save the partner first to upload a logo image.</Text>
										)}
									</Stack>
								</Box>
							</Collapse>
						</TabPanel>
						<TabPanel>
							<Stack spacing="4">
								<Stack direction="row" justify="space-between" align="center">
									<Heading size="md">Team Members</Heading>
									<Button size="sm" onClick={() => cmsApi.getTeamMembers().then(setTeamMembers)}>Refresh</Button>
								</Stack>
								<Text fontSize="xs" color="gray.500">Drag rows to reorder.</Text>
								<Box overflowX="auto">
									<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleTeamDragEnd}>
										<Table size="sm">
											<Thead><Tr><Th width="40px"></Th><Th>ID</Th><Th>Name</Th><Th>Role</Th><Th>Email</Th><Th>Actions</Th></Tr></Thead>
											<Tbody>
												<SortableContext items={teamMembers.map((t) => t.id)} strategy={verticalListSortingStrategy}>
													{teamMembers.map((item) => (
														<SortableRow key={item.id} item={item} columns={[{ key: "id" }, { key: "name" }, { key: "role" }, { key: "email" }]} onEdit={handleEditTeamMember} onDelete={handleDeleteTeamMember} />
													))}
												</SortableContext>
											</Tbody>
										</Table>
									</DndContext>
								</Box>
								{teamMembers.length === 0 && <Text color="gray.500" textAlign="center" py="4">No team members yet.</Text>}
							</Stack>
							<Stack direction="row" mt="4" spacing="2">
								<Button size="sm" leftIcon={<MdAdd />} onClick={() => { resetTeamMemberForm(); setShowAddTeamMember(!showAddTeamMember); }}>{editingTeamMember ? "Cancel Edit" : "Add New Team Member"}</Button>
								{editingTeamMember && <Button size="sm" variant="ghost" onClick={resetTeamMemberForm}>Cancel</Button>}
							</Stack>
							<Collapse in={showAddTeamMember}>
								<Box p="4" mt="2" bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
									<Stack spacing="3">
										<Input placeholder="Name" size="sm" value={newTeamMember.name} onChange={(e) => setNewTeamMember({ ...newTeamMember, name: e.target.value })} />
										<Input placeholder="Role" size="sm" value={newTeamMember.role} onChange={(e) => setNewTeamMember({ ...newTeamMember, role: e.target.value })} />
										<Input placeholder="Email" size="sm" value={newTeamMember.email} onChange={(e) => setNewTeamMember({ ...newTeamMember, email: e.target.value })} />
										<Input placeholder="Phone" size="sm" value={newTeamMember.phone} onChange={(e) => setNewTeamMember({ ...newTeamMember, phone: e.target.value })} />
										<Input placeholder="LinkedIn URL" size="sm" value={newTeamMember.linkedInUrl} onChange={(e) => setNewTeamMember({ ...newTeamMember, linkedInUrl: e.target.value })} />
										<Textarea placeholder="Highlights (JSON array or comma-separated)" size="sm" value={newTeamMember.highlights} onChange={(e) => setNewTeamMember({ ...newTeamMember, highlights: e.target.value })} />
										<Button size="sm" colorScheme="green" onClick={handleSaveTeamMember} isDisabled={!newTeamMember.name || isUploadingAvatar}>{editingTeamMember ? "Update" : "Create"}</Button>
										{editingTeamMember && (
											<Box p="3" bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
												<Text fontSize="sm" fontWeight="bold" color="gray.600" mb="2">Profile Picture</Text>
												<Stack direction="row" align="center" spacing="3">
													{isHttpUrl(editingTeamMember.avatarUrl) ? (
														<Image src={editingTeamMember.avatarUrl} alt={editingTeamMember.name} boxSize="48px" borderRadius="full" objectFit="cover" fallback={<Avatar name={editingTeamMember.name} size="md" bg="green.600" color="white" />} />
													) : (
														<Avatar name={editingTeamMember.name} size="md" bg="green.600" color="white" />
													)}
													<Stack spacing="1" flex="1">
														<input
															type="file"
															accept="image/jpeg,image/png,image/gif,image/webp"
															ref={avatarInputRef}
															style={{ display: "none" }}
															onChange={handleAvatarFileChange}
														/>
														<Stack direction="row" align="center" spacing="2">
															<Button size="xs" variant="outline" leftIcon={<MdUpload />} onClick={() => avatarInputRef.current?.click()}>
																{avatarFile ? "Change File" : "Choose File"}
															</Button>
															{avatarFile && (
																<>
																	<Text fontSize="xs" color="gray.500" isTruncated maxW="150px">{avatarFile.name}</Text>
																	<Button size="xs" colorScheme="green" onClick={handleUploadAvatar} isLoading={isUploadingAvatar} isDisabled={isUploadingAvatar}>Upload</Button>
																</>
															)}
														</Stack>
														<Text fontSize="xs" color="gray.400">JPG, PNG, GIF, WebP. Max 5MB.</Text>
													</Stack>
												</Stack>
											</Box>
										)}
										{!editingTeamMember && (
											<Text fontSize="xs" color="gray.400">Save the team member first to upload a profile picture.</Text>
										)}
									</Stack>
								</Box>
							</Collapse>
						</TabPanel>
						<TabPanel>
							<Stack spacing="4">
								<Stack direction="row" justify="space-between" align="center">
									<Heading size="md">Case Studies</Heading>
									<Button size="sm" onClick={() => cmsApi.getCaseStudies().then(setCaseStudies)}>Refresh</Button>
								</Stack>
								<Text fontSize="xs" color="gray.500">Drag rows to reorder.</Text>
								<Box overflowX="auto">
									<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCaseStudyDragEnd}>
										<Table size="sm">
											<Thead><Tr><Th width="40px"></Th><Th>ID</Th><Th>Name</Th><Th>Region</Th><Th>Actions</Th></Tr></Thead>
											<Tbody>
												<SortableContext items={caseStudies.map((t) => t.id)} strategy={verticalListSortingStrategy}>
													{caseStudies.map((item) => (
														<SortableRow key={item.id} item={item} columns={[{ key: "id" }, { key: "name" }, { key: "region" }]} onEdit={handleEditCaseStudy} onDelete={handleDeleteCaseStudy} />
													))}
												</SortableContext>
											</Tbody>
										</Table>
									</DndContext>
								</Box>
								{caseStudies.length === 0 && <Text color="gray.500" textAlign="center" py="4">No case studies yet.</Text>}
							</Stack>
							<Stack direction="row" mt="4" spacing="2">
								<Button size="sm" leftIcon={<MdAdd />} onClick={() => { resetCaseStudyForm(); setShowAddCaseStudy(!showAddCaseStudy); }}>{editingCaseStudy ? "Cancel Edit" : "Add New Case Study"}</Button>
								{editingCaseStudy && <Button size="sm" variant="ghost" onClick={resetCaseStudyForm}>Cancel</Button>}
							</Stack>
							<Collapse in={showAddCaseStudy}>
								<Box p="4" mt="2" bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
									<Stack spacing="3">
										<Input placeholder="Name" size="sm" value={newCaseStudy.name} onChange={(e) => setNewCaseStudy({ ...newCaseStudy, name: e.target.value })} />
										<Input placeholder="Region" size="sm" value={newCaseStudy.region} onChange={(e) => setNewCaseStudy({ ...newCaseStudy, region: e.target.value })} />
										<Textarea placeholder="Problem description" size="sm" value={newCaseStudy.problem} onChange={(e) => setNewCaseStudy({ ...newCaseStudy, problem: e.target.value })} />
										<Textarea placeholder="Action" size="sm" value={newCaseStudy.action} onChange={(e) => setNewCaseStudy({ ...newCaseStudy, action: e.target.value })} />
										<Input placeholder="Tags (comma-separated)" size="sm" value={newCaseStudy.tags} onChange={(e) => setNewCaseStudy({ ...newCaseStudy, tags: e.target.value })} />
										<Button size="sm" colorScheme="green" onClick={handleSaveCaseStudy} isDisabled={!newCaseStudy.name || isUploadingCsImage}>{editingCaseStudy ? "Update" : "Create"}</Button>
										{editingCaseStudy && (
											<Box p="3" bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
												<Text fontSize="sm" fontWeight="bold" color="gray.600" mb="3">Images ({caseStudyImages.length})</Text>
												{caseStudyImages.length > 0 && (
													<Stack spacing="2" mb="3">
														{caseStudyImages.map((img, idx) => (
															<Stack key={idx} direction={{ base: "column", md: "row" }} align={{ base: "stretch", md: "center" }} spacing="3" p="2" bg="white" borderRadius="md" border="1px solid" borderColor="gray.100">
																{img.imageUrl ? (
																	<Image src={img.imageUrl} alt={img.label || img.imageType} h="60px" w="80px" objectFit="contain" borderRadius="sm" border="1px solid" borderColor="gray.200" flexShrink={0} fallback={<Box h="60px" w="80px" bg="gray.200" borderRadius="sm" display="flex" alignItems="center" justifyContent="center" flexShrink={0}><Text fontSize="xs" color="gray.500">No img</Text></Box>} />
																) : (
																	<Box h="60px" w="80px" bg="gray.200" borderRadius="sm" display="flex" alignItems="center" justifyContent="center" flexShrink={0}><Text fontSize="xs" color="gray.500">No img</Text></Box>
																)}
																<Stack spacing="1" flex="1">
																	<Stack direction="row" spacing="2" align="center">
																		<Badge colorScheme={img.imageType === "weedHistory" ? "green" : "blue"} fontSize="xs">
																			{img.imageType === "weedHistory" ? "Weed History" : "Prescription"}
																		</Badge>
																		{img.label && <Text fontSize="xs" color="gray.500" isTruncated>{img.label}</Text>}
																	</Stack>
																	<Stack direction={{ base: "column", md: "row" }} spacing="2">
																		<Select size="xs" value={img.imageType} onChange={(e) => { const u = [...caseStudyImages]; u[idx] = { ...u[idx], imageType: e.target.value }; setCaseStudyImages(u); }} w="auto" minW="130px">
																			<option value="weedHistory">Weed History</option>
																			<option value="prescription">Prescription</option>
																		</Select>
																		<Input size="xs" placeholder="Label (e.g. 2023)" value={img.label || ""} onChange={(e) => { const u = [...caseStudyImages]; u[idx] = { ...u[idx], label: e.target.value }; setCaseStudyImages(u); }} />
																		<Input size="xs" placeholder="Alt text" value={img.altText || ""} onChange={(e) => { const u = [...caseStudyImages]; u[idx] = { ...u[idx], altText: e.target.value }; setCaseStudyImages(u); }} />
																	</Stack>
																</Stack>
																<Stack direction="row" spacing="1" flexShrink={0}>
																	<IconButton aria-label="Move up" icon={<span>&#9650;</span>} size="xs" variant="ghost" isDisabled={idx === 0} onClick={() => { const u = [...caseStudyImages]; [u[idx - 1], u[idx]] = [u[idx], u[idx - 1]]; setCaseStudyImages(u); }} />
																	<IconButton aria-label="Move down" icon={<span>&#9660;</span>} size="xs" variant="ghost" isDisabled={idx === caseStudyImages.length - 1} onClick={() => { const u = [...caseStudyImages]; [u[idx], u[idx + 1]] = [u[idx + 1], u[idx]]; setCaseStudyImages(u); }} />
																	<IconButton aria-label="Remove image" icon={<MdDelete />} size="sm" colorScheme="red" variant="ghost" onClick={() => setCaseStudyImages(caseStudyImages.filter((_, i) => i !== idx))} />
																</Stack>
															</Stack>
														))}
													</Stack>
												)}
												{caseStudyImages.length === 0 && <Text fontSize="xs" color="gray.400" mb="3">No images yet.</Text>}
												<Box p="2" bg="white" borderRadius="md" border="1px dashed" borderColor="gray.300">
													<Text fontSize="xs" fontWeight="bold" color="gray.500" mb="2">Add Image</Text>
													<Stack spacing="2">
														<Stack direction={{ base: "column", md: "row" }} spacing="2">
															<Select size="xs" value={newCsImageMeta.imageType} onChange={(e) => setNewCsImageMeta({ ...newCsImageMeta, imageType: e.target.value })} w="auto" minW="130px">
																<option value="weedHistory">Weed History</option>
																<option value="prescription">Prescription</option>
															</Select>
															<Input size="xs" placeholder="Label (e.g. 2023 or Herbicide Prescription)" flex="1" value={newCsImageMeta.label} onChange={(e) => setNewCsImageMeta({ ...newCsImageMeta, label: e.target.value })} />
															<Input size="xs" placeholder="Alt text" flex="1" value={newCsImageMeta.altText} onChange={(e) => setNewCsImageMeta({ ...newCsImageMeta, altText: e.target.value })} />
														</Stack>
														<Stack direction="row" spacing="2" align="center">
															<input type="file" accept="image/jpeg,image/png,image/gif,image/webp" ref={csImageInputRef} style={{ display: "none" }} onChange={handleCsImageFileChange} />
															<Button size="xs" variant="outline" leftIcon={<MdUpload />} onClick={() => csImageInputRef.current?.click()}>
																{csImageFile ? "Change File" : "Choose File"}
															</Button>
															{csImageFile && (
																<>
																	<Text fontSize="xs" color="gray.500" isTruncated maxW="150px">{csImageFile.name}</Text>
																	<Button size="xs" colorScheme="green" onClick={handleCsImageUpload} isLoading={isUploadingCsImage} isDisabled={isUploadingCsImage}>Upload &amp; Add</Button>
																</>
															)}
														</Stack>
													</Stack>
												</Box>
												<Text fontSize="xs" color="orange.500" mt="2">Click &quot;Update&quot; above to save image changes to the database.</Text>
											</Box>
										)}
										{!editingCaseStudy && (
											<Text fontSize="xs" color="gray.400">Save the case study first to manage images.</Text>
										)}
									</Stack>
								</Box>
							</Collapse>
						</TabPanel>
						<TabPanel>
							<Box p="4" bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" mb="6">
								<Text fontSize="sm" fontWeight="bold" color="gray.600" mb="2">Hero Background Image</Text>
								<Stack direction="row" align="center" spacing="4">
									{(() => {
										const heroSetting = siteSettings.find(s => s.key === "heroBackgroundImage");
										const heroUrl = heroSetting?.value;
										return heroUrl ? (
											<Image src={isHttpUrl(heroUrl) ? heroUrl : `/${heroUrl}`} alt="Hero background" h="80px" maxW="200px" objectFit="cover" borderRadius="md" border="1px solid" borderColor="gray.200" />
										) : (
											<Box w="200px" h="80px" bg="gray.200" borderRadius="md" display="flex" alignItems="center" justifyContent="center"><Text fontSize="xs" color="gray.500">No image</Text></Box>
										);
									})()}
									<Stack spacing="1" flex="1">
										<input
											type="file"
											accept="image/jpeg,image/png,image/gif,image/webp"
											ref={heroImageInputRef}
											style={{ display: "none" }}
											onChange={handleHeroImageFileChange}
										/>
										<Stack direction="row" align="center" spacing="2">
											<Button size="xs" variant="outline" leftIcon={<MdUpload />} onClick={() => heroImageInputRef.current?.click()}>
												{heroImageFile ? "Change File" : "Choose File"}
											</Button>
											{heroImageFile && (
												<>
													<Text fontSize="xs" color="gray.500" isTruncated maxW="150px">{heroImageFile.name}</Text>
													<Button size="xs" colorScheme="green" onClick={handleUploadHeroImage} isLoading={isUploadingHeroImage} isDisabled={isUploadingHeroImage}>Upload</Button>
												</>
											)}
										</Stack>
										<Text fontSize="xs" color="gray.400">JPG, PNG, GIF, WebP. Max 5MB. Recommended: 1920x1080 or larger.</Text>
									</Stack>
								</Stack>
							</Box>
							<CrudTable
								title="Site Settings"
								items={siteSettings}
								columns={[
									{ key: "key", label: "Key" },
									{ key: "value", label: "Value" },
									{ key: "description", label: "Description" },
								]}
								onDelete={handleDeleteSiteSetting}
								onEdit={handleEditSetting}
								onRefresh={() => cmsApi.getSiteSettings().then(setSiteSettings)}
								idField="key"
							/>
							<Stack direction="row" mt="4" spacing="2">
								<Button size="sm" leftIcon={<MdAdd />} onClick={() => { resetSettingForm(); setShowAddSetting(!showAddSetting); }}>{editingSetting ? "Cancel Edit" : "Add / Update Setting"}</Button>
								{editingSetting && <Button size="sm" variant="ghost" onClick={resetSettingForm}>Cancel</Button>}
							</Stack>
							<Collapse in={showAddSetting}>
								<Box p="4" mt="2" bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
									<Stack spacing="3">
										<Input placeholder="Key" size="sm" value={newSetting.key} onChange={(e) => setNewSetting({ ...newSetting, key: e.target.value })} isDisabled={!!editingSetting} />
										<Textarea placeholder="Value" size="sm" value={newSetting.value} onChange={(e) => setNewSetting({ ...newSetting, value: e.target.value })} />
										<Input placeholder="Description (optional)" size="sm" value={newSetting.description} onChange={(e) => setNewSetting({ ...newSetting, description: e.target.value })} />
										<Button size="sm" colorScheme="green" onClick={handleSaveSetting} isDisabled={!newSetting.key || !newSetting.value}>Save</Button>
									</Stack>
								</Box>
							</Collapse>
						</TabPanel>
						<TabPanel>
							<Stack spacing="4">
								<Stack direction="row" justify="space-between" align="center">
									<Heading size="md">Admin Users</Heading>
									<Button size="sm" onClick={() => adminApi.getUsers(getToken()).then(setCmsUsers)}>Refresh</Button>
								</Stack>
								<Box overflowX="auto">
									<Table size="sm">
										<Thead>
											<Tr>
												<Th>ID</Th>
												<Th>Email</Th>
												<Th>Actions</Th>
											</Tr>
										</Thead>
										<Tbody>
											{cmsUsers.map((u) => (
												<Tr key={u.id}>
													<Td>{u.id}</Td>
													<Td>{u.email}</Td>
													<Td>
														<IconButton
															aria-label="Delete user"
															icon={<MdDelete />}
															size="sm"
															colorScheme="red"
															variant="ghost"
															onClick={() => handleDeleteUser(u)}
														/>
													</Td>
												</Tr>
											))}
										</Tbody>
									</Table>
								</Box>
								{cmsUsers.length === 0 && (
									<Text color="gray.500" textAlign="center" py="4">No users configured.</Text>
								)}
								<Button size="sm" leftIcon={<MdAdd />} onClick={() => setShowAddUser(!showAddUser)}>Add User</Button>
								<Collapse in={showAddUser}>
									<Box p="4" mt="2" bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
										<Stack spacing="3">
											<Input placeholder="Email address" size="sm" type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} />
											<Button size="sm" colorScheme="green" onClick={handleAddUser} isDisabled={!newUserEmail}>Add User</Button>
										</Stack>
									</Box>
								</Collapse>
							</Stack>
						</TabPanel>
					</TabPanels>
				</Tabs>
				)}
			</Container>
		</Box>
	);
};
